<template>
  <ModalVue
    :modalActive="show"
    maxWidth="max-w-[min(808px,92vw)]"
    maxHeight="88vh"
    hasHeaderBorder
    hasFooterBorder
    @close="close"
  >
    <template #header>
      <div class="w-full pr-10">
        <EditableHeader v-model="modelName" placeholder="Nome do modelo" />
      </div>
    </template>

    <div
      data-testid="sheet-template-body"
      class="flex flex-col gap-5 p-4 w-full overflow-y-auto text-left"
    >
      <div
        v-if="notice.length"
        data-testid="sheet-template-format-notice"
        role="status"
        class="flex gap-3 rounded-lg border border-primary bg-primary5 px-4 py-3 text-sm text-neutral50"
      >
        <SVGIcon :path="mdiInformationOutline" class="h-5 w-5 shrink-0 text-primary" />
        <div class="flex flex-col gap-1">
          <p v-for="line in notice" :key="line">{{ line }}</p>
        </div>
      </div>

      <p class="text-sm text-neutral50">
        Configure as colunas do modelo de folha, de cima para baixo, na ordem que são exibidos. As
        colunas fixas são obrigatórias e não podem ser alteradas.
      </p>

      <section class="flex flex-col gap-2">
        <h3
          class="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-neutral50"
        >
          Colunas fixas
          <span class="rounded-full bg-primary5 px-2 py-0.5 text-xs font-bold text-primary">
            {{ fixedColumns.length }}
          </span>
        </h3>
        <div class="rounded-lg border border-gray-200 overflow-hidden">
          <div
            v-for="(column, index) in fixedColumns"
            :key="column"
            data-testid="fixed-column-row"
            class="flex items-center gap-3 border-b border-gray-100 bg-gray-50 px-3 py-2.5 last:border-b-0"
          >
            <span
              class="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-md bg-gray-200 text-xs font-extrabold text-neutral50"
            >
              {{ index + 1 }}
            </span>
            <span class="flex-1 text-sm font-medium text-neutral50">{{ column }}</span>
            <div class="flex w-[84px] justify-end">
              <LabelBadge :label="columnTypeLabel(column)" color="gray" size="small" :icon="null" />
            </div>
            <SVGIcon :path="mdiLock" class="h-5 w-5 shrink-0 text-neutralLight" />
            <span class="sr-only">Coluna fixa</span>
          </div>
        </div>
      </section>

      <section class="flex flex-col gap-2">
        <h3
          class="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-neutral50"
        >
          Colunas adicionais
          <span class="rounded-full bg-primary5 px-2 py-0.5 text-xs font-bold text-primary">
            {{ filledAdditional.length }}
          </span>
        </h3>

        <p
          v-if="!additional.length"
          data-testid="additional-columns-empty"
          class="rounded-lg border border-dashed border-gray-300 px-3 py-4 text-center text-sm text-gray-500"
        >
          Nenhuma coluna adicional. Use "Adicionar coluna" abaixo.
        </p>

        <div v-else ref="additionalList" class="rounded-lg border border-gray-200 overflow-hidden">
          <draggable
            v-model="additional"
            item-key="id"
            handle=".additional-column-handle"
            ghost-class="bg-primary5"
            animation="200"
            tag="div"
          >
            <template #item="{ element, index }">
              <div
                data-testid="additional-column-row"
                class="flex items-center gap-3 border-b border-gray-100 bg-white px-3 py-2 last:border-b-0"
              >
                <button
                  type="button"
                  data-testid="additional-column-handle"
                  class="additional-column-handle cursor-grab rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary20"
                  :aria-label="`${element.name || 'Coluna sem nome'}, posição ${fixedColumns.length + index + 1}. Use as setas para mover`"
                  @keydown="onHandleKeydown($event, index)"
                >
                  <SVGIcon :path="mdiDragVertical" class="h-5 w-5 text-neutralLight" />
                </button>
                <v-select
                  :model-value="element.name"
                  data-testid="additional-column-select"
                  :items="optionsFor(element)"
                  item-props
                  placeholder="Selecionar coluna"
                  variant="outlined"
                  density="compact"
                  hide-details
                  class="flex-1"
                  @update:model-value="setRowName(element, $event)"
                />
                <div class="flex w-[84px] justify-end">
                  <LabelBadge
                    v-if="element.name"
                    :label="badgeFor(element)"
                    color="gray"
                    size="small"
                    :icon="null"
                  />
                </div>
                <button
                  type="button"
                  data-testid="remove-column-button"
                  class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-negative hover:bg-red-50"
                  aria-label="Excluir coluna"
                  @click="additional.splice(index, 1)"
                >
                  <SVGIcon :path="mdiTrashCanOutline" class="h-5 w-5" />
                </button>
              </div>
            </template>
          </draggable>
        </div>

        <v-tooltip
          :disabled="!allOptionsUsed"
          text="Todas as colunas disponíveis já foram adicionadas"
          location="top"
        >
          <template #activator="{ props: tooltipProps }">
            <div v-bind="tooltipProps" class="w-full">
              <outlined-button
                data-testid="add-column-button"
                text="Adicionar coluna"
                width="100%"
                height="40px"
                class="text-primary border-primary"
                :disabled="allOptionsUsed"
                @click="addColumn"
              >
                <template #prepend>
                  <v-icon size="18" class="mr-[6px]">{{ mdiPlus }}</v-icon>
                </template>
              </outlined-button>
            </div>
          </template>
        </v-tooltip>
      </section>
    </div>

    <template #footer>
      <div class="flex justify-end gap-4">
        <outlined-button
          text="Fechar"
          width="100px"
          height="42px"
          class="text-primary border-primary"
          @click="close"
        />
        <filled-button
          data-testid="sheet-template-save-button"
          text="Salvar"
          width="100px"
          height="42px"
          color="primary"
          @click="save"
        />
      </div>
    </template>
  </ModalVue>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch, PropType } from 'vue'
import draggable from 'vuedraggable'
import {
  mdiDragVertical,
  mdiInformationOutline,
  mdiLock,
  mdiPlus,
  mdiTrashCanOutline
} from '@mdi/js'
import ModalVue from '@/components/designSystem/modals/Modal.vue'
import OutlinedButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import EditableHeader from '@/components/designSystem/Inputs/EditableHeader.vue'
import LabelBadge from '@/components/designSystem/Badges/LabelBadge.vue'
import SVGIcon from '@/components/SVGIcon.vue'
import { DSAttendanceMonitoringType } from '@/constants/ds-attendance.constants'
import {
  ADDITIONAL_COLUMN_OPTIONS,
  buildTemplateColumns,
  columnTypeLabel,
  fixedColumnsFor,
  formatNotice,
  loadTemplateColumns
} from '@/components/designSystem/modals/sheetTemplateColumns'

// Tipagens alinhadas com o Payload do Backend (contrato inalterado — #3052)
interface SheetTemplateFields {
  columns: string[]
  rows: number
}

interface SheetData {
  id?: number
  name: string
  templateFields: SheetTemplateFields
}

interface AdditionalColumn {
  id: number
  name: string
  /** Nome fora da lista fechada com que a linha foi carregada: segue oferecido só nesta linha. */
  originalName: string | null
}

const props = defineProps({
  show: { type: Boolean, required: true },
  initialData: { type: Object as PropType<SheetData | null>, default: null },
  monitoringType: {
    type: String as PropType<DSAttendanceMonitoringType>,
    default: DSAttendanceMonitoringType.FOLHA
  }
})

const emit = defineEmits(['close', 'save'])

const OPTIONS: readonly string[] = ADDITIONAL_COLUMN_OPTIONS

const modelName = ref('Novo Modelo de Folha')
const templateId = ref<number | undefined>(undefined)
const additional = ref<AdditionalColumn[]>([])
const notice = ref<string[]>([])
const additionalList = ref<HTMLElement | null>(null)
let nextRowId = 1

const fixedColumns = computed(() => fixedColumnsFor(props.monitoringType))

const isLegacyName = (name: string) => name !== '' && !OPTIONS.includes(name)

const toRow = (name: string): AdditionalColumn => ({
  id: nextRowId++,
  name,
  originalName: isLegacyName(name) ? name : null
})

watch(
  () => props.show,
  (isShowing) => {
    if (!isShowing) return
    if (props.initialData?.templateFields) {
      const loaded = loadTemplateColumns(
        props.initialData.templateFields.columns,
        props.monitoringType
      )
      templateId.value = props.initialData.id
      modelName.value = props.initialData.name
      additional.value = loaded.additional.map(toRow)
      notice.value = formatNotice(loaded, props.monitoringType)
    } else {
      templateId.value = undefined
      modelName.value = 'Novo Modelo de Folha'
      additional.value = []
      notice.value = []
    }
  },
  // Montado já aberto (ex.: stories) também carrega o estado.
  { immediate: true }
)

const filledAdditional = computed(() => additional.value.filter((row) => row.name.trim() !== ''))
// "Antiga" vale pelo nome ATUAL da linha: trocada para uma opção, passa a ocupar essa opção (Q6).
const usedOptions = computed(() =>
  additional.value.filter((row) => OPTIONS.includes(row.name)).map((row) => row.name)
)
// Linhas vazias contam: cada uma ainda vai receber uma das opções (Q6).
const allOptionsUsed = computed(
  () => additional.value.filter((row) => !isLegacyName(row.name)).length >= OPTIONS.length
)

const optionsFor = (row: AdditionalColumn) => {
  const options = OPTIONS.map((option) => ({
    title: option,
    value: option,
    disabled: option !== row.name && usedOptions.value.includes(option)
  }))
  return row.originalName
    ? [{ title: row.originalName, value: row.originalName, disabled: false }, ...options]
    : options
}

// O type-ahead do VSelect 3.3 escolhe pelo prefixo do título ignorando `disabled` (Q6):
// valor em uso em outra linha é recusado; o nome antigo da própria linha continua permitido.
const setRowName = (row: AdditionalColumn, value: string | null) => {
  const name = value ?? ''
  const usedElsewhere = additional.value.some((other) => other !== row && other.name === name)
  if (name !== '' && name !== row.originalName && usedElsewhere) return
  row.name = name
}

// Nome livre antigo é sempre texto (Q2), mesmo que o nome lembre uma coluna de hora.
const badgeFor = (row: AdditionalColumn) =>
  isLegacyName(row.name) ? 'Texto' : columnTypeLabel(row.name)

const addColumn = () => {
  if (allOptionsUsed.value) return
  additional.value.push(toRow(''))
}

// ↑/↓ na alça move uma posição, só dentro das adicionais (padrão do SourcePrecedenceList).
const onHandleKeydown = (event: KeyboardEvent, index: number) => {
  if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return
  event.preventDefault()
  const target = event.key === 'ArrowUp' ? index - 1 : index + 1
  if (target < 0 || target >= additional.value.length) return
  const next = [...additional.value]
  const [moved] = next.splice(index, 1)
  next.splice(target, 0, moved)
  additional.value = next
  // A linha movida é recriada no DOM e perde o foco; devolve o foco à alça dela.
  nextTick(() => {
    const handles = additionalList.value?.querySelectorAll<HTMLElement>(
      '[data-testid="additional-column-handle"]'
    )
    handles?.[target]?.focus()
  })
}

const close = () => emit('close')

const save = () => {
  emit('save', {
    id: templateId.value,
    name: modelName.value,
    templateFields: {
      columns: buildTemplateColumns(
        props.monitoringType,
        additional.value.map((row) => row.name)
      ),
      // RN-09: o modelo não dimensiona linhas; edição preserva o valor salvo, criação nasce com 1.
      rows: props.initialData?.templateFields?.rows ?? 1
    }
  })
}
</script>
